import React, { useState, useEffect } from 'react'
import { isNilOrEmpty, useDebounce } from 'packages/core'
import { InputBase, Paper, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import { usePageContext } from 'pageContext'

const StyledInput = styled(InputBase)(({ theme }) => ({
    '&.MuiInputBase-root': {
        width: '100% !important',
        height: '100% !important',
        borderRadius: '5px',
    },
    '& .MuiInputBase-input': {
        position: 'relative',
        fontSize: 16,
        color: '#aab0b4',
        padding: '9.5px 6px',
    },
    '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
    },
}))

const StyledPaper = styled(Paper)(({ theme, outlined }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#ffffff !important',
    boxShadow: outlined
        ? 'none !important'
        : '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    border: outlined ? 'solid 1px #d8d8dd' : 'solid 1px #ebebed',
    borderRadius: outlined ? '3px' : '5px',
    padding: outlined ? '0px 0px 0px 10px' : '2px 4px',
    height: outlined ? 'initial' : '40px',
    fontSize: outlined ? '13px' : 'initial',
    '& .MuiInputBase-input': {
        fontSize: outlined ? '13px !important' : 'initial',
    },
    '&:hover': {
        border: outlined
            ? `1px solid ${theme?.palette?.primary?.main}`
            : 'initial',
    },
    '&:focus-within': {
        border: outlined
            ? `1px solid ${theme?.palette?.primary?.main}`
            : 'initial',
        boxShadow: outlined
            ? '0 4px 8px 0 rgba(48, 127, 193, 0.33)'
            : 'initial',
    },
}))

const StyledIcon = styled(Icon)({
    width: 19,
    height: 19,
    marginLeft: 17,
})

const StyledClearIcon = styled(Icon)({
    margin: '0px 10px !important',
    '&:hover': {
        cursor: 'pointer',
    },
})

const Search = (props) => {
    const { handleSearch, handleClear, placeholder, value, outlined } = props
    const { t } = useTranslation()
    const [searchKey, setSearchKey] = useState(value)
    const [, dispatch] = usePageContext()

    useEffect(() => {
        if (value !== searchKey) {
            setSearchKey(value)
        }
    }, [value])

    const handleChange = (event) => {
        const keyword = event.target.value
        setSearchKey(keyword)
        dispatch({
            type: 'SET_PAGE',
            payload: 1,
        })
    }
    const handleClearSearch = () => {
        setSearchKey('')
        handleClear('')
        dispatch({
            type: 'SET_PAGE',
            payload: 1,
        })
    }
    const debouncedValue = useDebounce(searchKey)

    useEffect(() => {
        handleSearch(debouncedValue)
    }, [debouncedValue])

    return (
        <StyledPaper outlined={outlined}>
            <StyledIcon name="Search" color="#D8D8DD" />

            <StyledInput
                value={searchKey}
                onChange={handleChange}
                placeholder={placeholder || t('Search')}
                inputProps={{
                    'aria-label': placeholder || 'Search',
                }}
            />

            {!isNilOrEmpty(searchKey) && (
                <StyledClearIcon
                    name="Close"
                    color="#D8D8DD"
                    role="button"
                    tabIndex={0}
                    aria-label="Clear"
                    onClick={handleClearSearch}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            handleClearSearch()
                        }
                    }}
                    data-ai-explain="Clear search"
                />
            )}
        </StyledPaper>
    )
}

export default Search
